import type { Category } from "@CommonApi/categories";
import { useQuery } from "@tanstack/react-query";
import { RouteCategories_categoryId_PdfSummaryExclusions } from "@CommonApi/categories/_categoryId_/pdf-summary-exclusions";
import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useApi } from "src/api";
import { FileUploader } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Modal, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { downloadFile } from "src/utils/downloadFile";
import { importPdfSummaryExclusionsModalDataAtom } from "./store/atoms";

const PDF_SUMMARY_EXCLUSIONS_HEADER = "PRODUCT_CODE";
const MAX_PRODUCT_CODE_LENGTH = 256;

const parseSingleCsvCell = (line: string): string | null => {
  const trimmedLine = line.trim();
  if (!trimmedLine.startsWith('"')) {
    return /[",]/.test(trimmedLine) ? null : trimmedLine;
  }

  let value = "";
  for (let index = 1; index < trimmedLine.length; index += 1) {
    const character = trimmedLine[index];
    if (character !== '"') {
      value += character;
      continue;
    }

    if (trimmedLine[index + 1] === '"') {
      value += '"';
      index += 1;
      continue;
    }

    if (trimmedLine.slice(index + 1).trim()) return null;
    return value.includes(",") ? null : value.trim();
  }

  return null;
};

const isValidPdfSummaryExclusionsCsv = (contents: string): boolean => {
  const rows = contents
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((row) => row.trim());
  const header = rows.length > 0 ? parseSingleCsvCell(rows[0]) : null;

  if (header?.toUpperCase() !== PDF_SUMMARY_EXCLUSIONS_HEADER) return false;

  return rows.slice(1).every((row) => {
    const productCode = parseSingleCsvCell(row);
    return (
      productCode !== null &&
      productCode.length > 0 &&
      productCode.length <= MAX_PRODUCT_CODE_LENGTH
    );
  });
};

export const ModalImportPdfSummaryExclusions = () => {
  const [category, setCategory] = useAtom(importPdfSummaryExclusionsModalDataAtom);

  useEffect(() => {
    return () => setCategory(null);
  }, []);

  return category ? <ModalContent category={category} /> : null;
};

const ModalContent = ({ category }: { category: Category }) => {
  const { getPdfSummaryProductExclusionsApi, postPdfSummaryProductExclusionsApi } = useApi();
  const { hasLoader } = useLoaders();
  const { t } = useTranslation("planogramDelivery");
  const [file, setFile] = useState<File | null>(null);
  const [productCount, setProductCount] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidatingFile, setIsValidatingFile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const validationRequestId = useRef(0);
  const isLoading = hasLoader(RouteCategories_categoryId_PdfSummaryExclusions);

  useEffect(() => {
    return () => {
      validationRequestId.current += 1;
    };
  }, []);
  const exclusionsQuery = useQuery({
    queryKey: ["pdf-summary-product-exclusions", category.id],
    queryFn: async () => {
      const response = await getPdfSummaryProductExclusionsApi(category.id);
      if (!response) throw new Error("Failed to load PDF summary exclusions");
      return response;
    },
  });

  const upload = async () => {
    if (!file || isUploading) return;

    setIsUploading(true);
    try {
      const response = await postPdfSummaryProductExclusionsApi(category.id, { file });
      if (response) {
        setProductCount(response.product_count);
        await exclusionsQuery.refetch();
      }
    } finally {
      setIsUploading(false);
    }
  };

  const validateFile = async (selectedFile: File) => {
    const requestId = validationRequestId.current + 1;
    validationRequestId.current = requestId;
    setFile(null);
    setValidationError(null);
    setIsValidatingFile(true);

    try {
      const contents = await selectedFile.text();
      if (validationRequestId.current !== requestId) return;

      if (!isValidPdfSummaryExclusionsCsv(contents)) {
        setValidationError(t("pdfSummaryExclusions.invalidCsv"));
        return;
      }

      setFile(selectedFile);
    } catch {
      if (validationRequestId.current === requestId) {
        setValidationError(t("pdfSummaryExclusions.invalidCsv"));
      }
    } finally {
      if (validationRequestId.current === requestId) {
        setIsValidatingFile(false);
      }
    }
  };

  const downloadSample = () => {
    downloadFile({
      name: "pdf-summary-exclusions-sample",
      extension: "csv",
      data: "PRODUCT_CODE\n",
    });
  };

  const downloadCurrentList = () => {
    const productCodes = exclusionsQuery.data?.product_codes ?? [];
    const rows = productCodes.map((code) =>
      /[",\r\n]/.test(code) ? `"${code.replace(/"/g, '""')}"` : code,
    );
    downloadFile({
      name: `pdf-summary-exclusions-${category.code}`,
      extension: "csv",
      data: `PRODUCT_CODE\n${rows.join("\n")}\n`,
    });
  };

  return (
    <Modal
      name="ModalImportPdfSummaryExclusions"
      title={t("pdfSummaryExclusions.modalTitle", { categoryCode: category.code })}
      width="500px"
    >
      <Flex column>
        <Flex padding="10px" column gap="16px">
          <Text variant="small1" color={Color.textSecondary}>
            {t("pdfSummaryExclusions.description")}
          </Text>
          <FileUploader
            fileLabel={t("pdfSummaryExclusions.fileLabel")}
            fileType="csv"
            isLoading={isLoading || isUploading}
            onChange={validateFile}
            onSuccess={
              productCount === null
                ? undefined
                : t("pdfSummaryExclusions.success", { count: productCount })
            }
          />
          {validationError && (
            <Text variant="small1" color={Color.red}>
              {validationError}
            </Text>
          )}
        </Flex>
        <Flex
          padding="10px"
          borderTop={`1px solid ${Color.spaceGray}`}
          justify="between"
          align="center"
        >
          <Flex gap="8px">
            <Button
              variant="borderOnly"
              isDisabled={
                isLoading ||
                isValidatingFile ||
                isUploading ||
                exclusionsQuery.isFetching ||
                exclusionsQuery.isError
              }
              onClick={downloadCurrentList}
            >
              {t("pdfSummaryExclusions.downloadCurrentList")}
            </Button>
            <Button variant="borderOnly" onClick={downloadSample}>
              {t("pdfSummaryExclusions.downloadSample")}
            </Button>
          </Flex>
          <Button
            isDisabled={
              !file || isLoading || isValidatingFile || isUploading || productCount !== null
            }
            isLoading={isLoading || isValidatingFile || isUploading}
            onClick={upload}
          >
            {t("pdfSummaryExclusions.upload")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
